/**
 * Images in documents.
 *
 * Display: a markdown link is relative to the document, and the page is served
 * from md.ekram.io, so `assets/pic.png` would load from the server and fail.
 * Relative links are read through the open folder's handle instead and shown
 * as blob: URLs. The markdown keeps the relative link.
 *
 * A link that climbs out of the folder resolves to nothing: the folder handle
 * is the boundary, and `insideFolder` refuses `..` past its root.
 *
 * Pasting: an image on the clipboard has no file, so it is written into the
 * document's assets folder and linked relatively, as on the desktop. That needs
 * a folder opened with write access; elsewhere the user is told why not.
 */
import type { SaveImageRequest, SaveImageResult } from './types'
import { directoryFor, fileFor, folderOf, PlatformError } from './handles'
import { parsePath } from './paths'

/** Left alone: already absolute, or not a path at all. */
const ABSOLUTE = /^(https?:|data:|blob:|\/\/)/i

/** Resolved blob URLs by the file path they show, with the file's time. */
const cache = new Map<string, { url: string; mtimeMs: number }>()
/** The Blob behind each URL this page made, for inlining into an export. */
const blobs = new Map<string, Blob>()

/** The same picture the desktop showed for an image it could not load. */
function placeholder(text: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="56" viewBox="0 0 320 56">` +
    `<rect width="320" height="56" rx="6" fill="#8884" />` +
    `<text x="160" y="33" font-family="sans-serif" font-size="12" text-anchor="middle" fill="#666">${text}</text>` +
    `</svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

const NEEDS_FOLDER = placeholder('Open the document’s folder to show this image')
const NOT_FOUND = placeholder('Image not found')

/** Turns a relative link into the path of the file it names, or null. */
export function imagePath(src: string, documentDir: string | null): string | null {
  if (!documentDir) return null
  let decoded = src.replace(/^\.\//, '')
  try {
    decoded = decodeURI(decoded)
  } catch {
    /* keep as written */
  }
  // Query strings and fragments are not part of a file's name.
  decoded = decoded.replace(/[?#].*$/, '')
  return `${documentDir.replace(/\/+$/, '')}/${decoded}`
}

/**
 * The URL to display for an image link. Never throws: a link that cannot be
 * shown gets a picture that says why.
 */
export async function resolveImage(src: string, documentDir: string | null): Promise<string> {
  if (!src) return src
  if (ABSOLUTE.test(src)) return src
  // A Windows path or a file: URL from a document written on the desktop.
  if (/^[a-zA-Z]:[\\/]/.test(src) || /^file:/i.test(src)) return NEEDS_FOLDER
  const dir = documentDir ? parsePath(documentDir) : null
  if (!dir || (dir.scheme !== 'ws' && dir.scheme !== 'snap')) return NEEDS_FOLDER
  const path = imagePath(src, documentDir)
  if (!path) return NEEDS_FOLDER
  try {
    const file = await fileFor(path, { ask: false })
    const hit = cache.get(path)
    if (hit && hit.mtimeMs === file.lastModified) return hit.url
    if (hit) {
      URL.revokeObjectURL(hit.url)
      blobs.delete(hit.url)
    }
    const url = URL.createObjectURL(file)
    cache.set(path, { url, mtimeMs: file.lastModified })
    blobs.set(url, file)
    return url
  } catch (err) {
    return /EGRANT/.test(String(err)) ? NEEDS_FOLDER : NOT_FOUND
  }
}

/** The Blob behind a URL this page made, or undefined. */
export function blobFor(url: string): Blob | undefined {
  return blobs.get(url)
}

const MIME_EXTENSIONS: Record<string, string> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'image/svg+xml': '.svg',
  'image/bmp': '.bmp',
  'image/avif': '.avif',
}

/** Strips anything awkward in a file name or a markdown link. */
function sanitize(name: string): string {
  return name
    .replace(/\.[^.]+$/, '')
    .replace(/[^\w\-. ]+/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60)
}

function timestamp(): string {
  const d = new Date()
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
}

async function taken(dir: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await dir.getFileHandle(name)
    return true
  } catch {
    return false
  }
}

/** Writes a pasted or dropped image beside the document. Never overwrites. */
export async function saveImage(req: SaveImageRequest): Promise<SaveImageResult> {
  try {
    if (!req.documentPath) {
      return { ok: false, error: 'Save the document before adding images to it.' }
    }
    const doc = parsePath(req.documentPath)
    const docDir = doc ? folderOf(doc) : null
    if (!doc || doc.scheme !== 'ws' || !docDir) {
      return {
        ok: false,
        error:
          doc?.scheme === 'snap'
            ? 'This folder was opened read-only, because this browser cannot write files. Images cannot be added here.'
            : 'Open the document’s folder with File ▸ Open Folder to add images to it. The browser only lets the page write inside a folder you have opened.',
      }
    }
    const fromName = req.suggestedName?.match(/\.[^.]+$/)?.[0]?.toLowerCase() ?? ''
    const ext = MIME_EXTENSIONS[req.mimeType] ?? (fromName || '.png')
    const base = (req.suggestedName && sanitize(req.suggestedName)) || `image-${timestamp()}`

    const folder = req.assetsFolder.replace(/^[\\/]+|[\\/]+$/g, '').replace(/\\/g, '/') || 'assets'
    if (folder.split('/').some((s) => s === '..')) {
      throw new PlatformError('EINVAL', 'The assets folder must be inside the document’s folder.')
    }
    const target = await directoryFor(`${docDir}/${folder}`, 'readwrite', { create: true })
    let name = `${base}${ext}`
    for (let n = 1; await taken(target, name); n++) name = `${base}-${n}${ext}`
    const handle = await target.getFileHandle(name, { create: true })
    const w = await handle.createWritable()
    await w.write(req.data as BufferSource)
    await w.close()
    // Forward slashes: markdown links use them everywhere.
    const relativePath = `${folder}/${name}`
    return { ok: true, relativePath, absolutePath: `${docDir}/${relativePath}` }
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message.replace(/^E[A-Z]+: /, '') : String(err),
    }
  }
}
